/**
 * Rate-limit retry helpers shared by every HTTP client in the app.
 *
 * The functions are pure so they can be unit tested without mocks. Each HTTP
 * client (fetch-based ApiClient, axios instance, statementExecutionService)
 * imports these and supplies its own request-replay primitive.
 */

export const RATE_LIMIT_STATUS = 429;
export const RATE_LIMIT_MAX_RETRIES = 3;

const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 5000;
const JITTER_MS = 200;

/**
 * Parse a `Retry-After` header value to milliseconds-to-wait.
 *
 * Accepts:
 *   - Delta-seconds (integer or decimal): "1", "0.5"
 *   - HTTP-date: "Mon, 06 May 2026 10:00:00 GMT"
 *
 * Returns `null` when the value is missing or unparseable, signalling that the
 * caller should fall back to `computeBackoffMs(attempt)`.
 *
 * Negative deltas are clamped to 0 so a server can short-circuit the wait.
 */
export const parseRetryAfterMs = (
  headerValue: string | null | undefined,
  now: number = Date.now()
): number | null => {
  if (!headerValue) return null;
  const trimmed = headerValue.trim();
  if (!trimmed) return null;

  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber)) {
    return Math.max(0, Math.round(asNumber * 1000));
  }

  const asDate = Date.parse(trimmed);
  if (Number.isFinite(asDate)) {
    return Math.max(0, asDate - now);
  }

  return null;
};

/**
 * Capped exponential backoff with full jitter. Attempt is 0-indexed so the
 * first retry waits ~500ms ± jitter, the second ~1000ms ± jitter, etc.
 */
export const computeBackoffMs = (attempt: number, random: () => number = Math.random): number => {
  const safeAttempt = Math.max(0, Math.floor(attempt));
  const exponential = BASE_BACKOFF_MS * 2 ** safeAttempt;
  const capped = Math.min(MAX_BACKOFF_MS, exponential);
  const jitter = (random() * 2 - 1) * JITTER_MS;
  return Math.max(0, Math.round(capped + jitter));
};

/**
 * Resolve the wait time for an attempt: prefer Retry-After when supplied
 * (server knows best), otherwise fall back to client-side backoff.
 */
export const resolveRetryDelayMs = (
  headerValue: string | null | undefined,
  attempt: number,
  now: number = Date.now(),
  random: () => number = Math.random
): number => {
  const fromHeader = parseRetryAfterMs(headerValue, now);
  return fromHeader !== null ? fromHeader : computeBackoffMs(attempt, random);
};
